import { useMemo, useState } from 'react';
import type { HTMLAttributes, Key } from 'react';
import { Button, Card, Table, Tag, Tooltip, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { HistoryOutlined } from '@ant-design/icons';

import type {
	MaterialMovementRow,
	MovementReportBar,
} from '../../../../redux/api/material-movements';
import ActionTag from '../../../../components/MaterialMovements/ActionTag';
import {
	type LocationFormatter,
	formatLengthChange,
	formatLocationChange,
	formatShopTime,
	operatorName,
} from '../../../../components/MaterialMovements/movementMeta';
import type { AuditBar, AuditLine, AuditSection } from './auditLines';
import { formatOperators } from './auditLines';

const { Text } = Typography;

const HISTORICAL_HINT =
	'Rebuilt from records made before movement logging started. The from-location and length at the time may be missing.';

const NOW_AT_HINT = 'Where the system says this bar is right now. Check it is physically here.';

/** Rows of one tree table: material headings, audit lines, their bars, and movements. */
type Row =
	| { kind: 'section'; key: string; section: AuditSection }
	| { kind: 'line'; key: string; line: AuditLine; children?: Row[] }
	| { kind: 'bar'; key: string; item: AuditBar; children?: Row[] }
	| { kind: 'movement'; key: string; row: MaterialMovementRow };

const movementRows = (bar: MovementReportBar): Row[] | undefined =>
	bar.movements.length > 1
		? bar.movements.map((row) => ({ kind: 'movement' as const, key: `m:${row.id}`, row }))
		: undefined;

const toRows = (sections: AuditSection[]): Row[] =>
	sections.flatMap((section) => [
		{ kind: 'section' as const, key: `s:${section.key}`, section },
		...section.lines.map((line): Row => {
			const children: Row[] | undefined =
				line.bars.length > 1
					? line.bars.map((item) => ({
							kind: 'bar' as const,
							key: `b:${item.key}`,
							item,
							children: movementRows(item.bar),
						}))
					: movementRows(line.bars[0].bar);
			return { kind: 'line', key: `l:${line.key}`, line, children };
		}),
	]);

const collectExpandable = (rows: Row[]): Key[] =>
	rows.flatMap((row) =>
		'children' in row && row.children ? [row.key, ...collectExpandable(row.children)] : []
	);

const joinParts = (parts: (string | false | null | undefined)[]) => parts.filter(Boolean).join(' · ');

const HistoryButton = ({
	bar,
	onOpenHistory,
}: {
	bar: MovementReportBar;
	onOpenHistory: (bar: MovementReportBar) => void;
}) =>
	bar.id ? (
		<Tooltip title="Full history">
			<Button
				size="small"
				type="text"
				icon={<HistoryOutlined />}
				aria-label={`Full history of ${bar.friendly_id}`}
				onClick={(event) => {
					event.stopPropagation();
					onOpenHistory(bar);
				}}
			/>
		</Tooltip>
	) : null;

const NowAt = ({ text }: { text: string | null }) =>
	text ? (
		<Tooltip title={NOW_AT_HINT}>
			<Tag color="warning" className="ml-1">
				{text}
			</Tag>
		</Tooltip>
	) : null;

const Historical = () => (
	<Tooltip title={HISTORICAL_HINT}>
		<Tag className="ml-1">historical</Tag>
	</Tooltip>
);

const Notes = ({ text, historical }: { text: string; historical?: boolean }) => (
	<span className="flex items-center">
		<Text ellipsis={{ tooltip: text }} className="min-w-0">
			{text}
		</Text>
		{historical && <Historical />}
	</span>
);

const buildColumns = (
	showDate: boolean,
	formatLocation: LocationFormatter,
	onOpenHistory: (bar: MovementReportBar) => void
): ColumnsType<Row> => {
	const spanSection = (row: Row) => ({ colSpan: row.kind === 'section' ? 0 : 1 });
	return [
		{
			title: 'Bars',
			key: 'bars',
			width: 220,
			onCell: (row) => ({ colSpan: row.kind === 'section' ? 7 : 1 }),
			render: (_: unknown, row) => {
				if (row.kind === 'section') {
					const { materialName, barCount } = row.section;
					return (
						<span>
							<Text strong>{materialName}</Text>
							<Text type="secondary">
								{' '}
								· {barCount} {barCount === 1 ? 'bar' : 'bars'}
							</Text>
						</span>
					);
				}
				if (row.kind === 'movement') return null;
				const bar = row.kind === 'bar' ? row.item.bar : row.line.bars[0].bar;
				const single = row.kind === 'bar' || row.line.bars.length === 1;
				return (
					<span>
						<Text strong className="font-mono">
							{row.kind === 'bar' ? bar.friendly_id : row.line.barIds}
						</Text>
						{single && <HistoryButton bar={bar} onOpenHistory={onOpenHistory} />}
					</span>
				);
			},
		},
		{
			title: 'When (CT)',
			key: 'when',
			width: 170,
			onCell: spanSection,
			render: (_: unknown, row) => {
				if (row.kind === 'line') return <span className="whitespace-nowrap">{row.line.timeSpan}</span>;
				if (row.kind === 'bar') return <span className="whitespace-nowrap">{row.item.summary.timeSpan}</span>;
				if (row.kind === 'movement') {
					return (
						<span className="whitespace-nowrap">
							{formatShopTime(row.row.occurred_at, showDate ? 'M/D h:mm A' : 'h:mm A')}
						</span>
					);
				}
				return null;
			},
		},
		{
			title: 'Activity',
			key: 'activity',
			width: 190,
			onCell: spanSection,
			render: (_: unknown, row) => {
				if (row.kind === 'movement') return <ActionTag row={row.row} />;
				if (row.kind === 'line') return row.line.activity;
				if (row.kind === 'bar') return row.item.summary.actionChain;
				return null;
			},
		},
		{
			title: 'Length',
			key: 'length',
			width: 160,
			onCell: spanSection,
			render: (_: unknown, row) => {
				const text =
					row.kind === 'line'
						? row.line.length
						: row.kind === 'bar'
							? row.item.summary.netLength
							: row.kind === 'movement'
								? formatLengthChange(row.row)
								: null;
				return text && <span className="whitespace-nowrap">{text}</span>;
			},
		},
		{
			title: 'Location',
			key: 'location',
			width: 210,
			onCell: spanSection,
			render: (_: unknown, row) => {
				if (row.kind === 'movement') return formatLocationChange(row.row, formatLocation);
				if (row.kind === 'bar') return row.item.summary.netLocation;
				if (row.kind === 'line') {
					return (
						<span>
							{row.line.location}
							<NowAt text={row.line.nowAt} />
						</span>
					);
				}
				return null;
			},
		},
		{
			title: 'Operator',
			key: 'operator',
			width: 150,
			onCell: spanSection,
			render: (_: unknown, row) => {
				if (row.kind === 'movement') {
					const { recorded_by: recordedBy } = row.row;
					const name = operatorName(row.row);
					return recordedBy && recordedBy.name !== name ? (
						<Tooltip title={`Logged in as ${recordedBy.name}`}>
							<span className="cursor-help underline decoration-dotted">{name}</span>
						</Tooltip>
					) : (
						name
					);
				}
				const names =
					row.kind === 'line'
						? row.line.operatorNames
						: row.kind === 'bar'
							? row.item.summary.operators
							: [];
				if (row.kind === 'section') return null;
				return names.length > 1 ? (
					<Tooltip title={names.join(', ')}>
						<span className="cursor-help underline decoration-dotted">{formatOperators(names)}</span>
					</Tooltip>
				) : (
					formatOperators(names)
				);
			},
		},
		{
			title: 'Job / notes',
			key: 'notes',
			width: 260,
			onCell: spanSection,
			render: (_: unknown, row) => {
				if (row.kind === 'movement') {
					return (
						<Notes
							text={joinParts([
								row.row.order_line_item?.name,
								row.row.parent_bar_friendly_id && `from ${row.row.parent_bar_friendly_id}`,
								row.row.notes,
							])}
						/>
					);
				}
				if (row.kind === 'line') return <Notes text={row.line.notes} historical={row.line.historical} />;
				if (row.kind === 'bar') {
					const { summary, familyRoot } = row.item;
					const parents = summary.parentBars.length ? summary.parentBars : familyRoot ? [familyRoot] : [];
					return (
						<Notes
							text={joinParts([...summary.jobs, parents.length > 0 && `from ${parents.join(', ')}`])}
							historical={summary.hasBackfilled}
						/>
					);
				}
				return null;
			},
		},
	];
};

interface BarGroupsProps {
	sections: AuditSection[];
	showDate: boolean;
	formatLocation: LocationFormatter;
	onOpenHistory: (bar: MovementReportBar) => void;
}

/** One compact table for the whole report: the same lines as the printout, expandable to bars and movements. */
const BarGroups = ({ sections, showDate, formatLocation, onOpenHistory }: BarGroupsProps) => {
	const rows = useMemo(() => toRows(sections), [sections]);
	const columns = useMemo(
		() => buildColumns(showDate, formatLocation, onOpenHistory),
		[showDate, formatLocation, onOpenHistory]
	);
	const expandableKeys = useMemo(() => collectExpandable(rows), [rows]);
	const [expanded, setExpanded] = useState<Key[]>([]);
	const allExpanded = expandableKeys.length > 0 && expanded.length >= expandableKeys.length;
	const lineCount = sections.reduce((total, s) => total + s.lines.length, 0);

	return (
		<Card
			size="small"
			className="shadow-sm"
			title={`${lineCount} ${lineCount === 1 ? 'line' : 'lines'}`}
			extra={
				expandableKeys.length > 0 && (
					<Button size="small" type="link" onClick={() => setExpanded(allExpanded ? [] : expandableKeys)}>
						{allExpanded ? 'Collapse all' : 'Expand all'}
					</Button>
				)
			}
		>
			<Table<Row>
				size="small"
				rowKey="key"
				sticky
				columns={columns}
				dataSource={rows}
				pagination={false}
				scroll={{ x: 1360 }}
				rowClassName={(row) =>
					row.kind === 'section'
						? 'bg-gray-100'
						: row.kind === 'movement'
							? 'bg-gray-50 text-xs'
							: ''
				}
				onRow={(row) => {
					if (row.kind === 'bar') return { 'data-testid': `movement-bar-${row.item.bar.friendly_id}` } as HTMLAttributes<HTMLElement>;
					if (row.kind === 'line') {
						return {
							'data-testid': `movement-line-${row.line.bars.map((b) => b.bar.friendly_id).join('-')}`,
						} as HTMLAttributes<HTMLElement>;
					}
					return {};
				}}
				expandable={{
					expandedRowKeys: expanded,
					onExpandedRowsChange: (keys) => setExpanded([...keys]),
					indentSize: 16,
				}}
			/>
		</Card>
	);
};

export default BarGroups;
